import { FC, useEffect } from 'react'

import { cn } from '@bem-react/classname'

import { Footer } from '@/components/Layout/Footer'
import { Header } from '@/components/Layout/Header'
import { Tag } from '@/components/UI/Tag'

import './style.scss'

const cnKyc = cn('Policy')

export const KYC: FC = (): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className={cnKyc()}>
      <Header isHome />
      <div className={cnKyc('wrapper')}>
        <Tag className={cnKyc('title')}>
          KYC/AML Policy for <span>exstream.cc</span>
        </Tag>
        <div className={cnKyc('info')}>Last updated January 10, 2025</div>
        <h2 className={cnKyc('subtitle')}>1. Introduction</h2>
        <p className={cnKyc('text')}>
          The KYC/AML (Know Your Customer / Anti-Money Laundering) Policy of
          exstream.cc is designed to prevent the use of our services for illegal
          activities, including money laundering, terrorist financing, and
          fraud. We are committed to complying with all applicable laws and
          regulations governing these areas, ensuring the integrity of our
          operations and the safety of our customers.
        </p>
        <h2 className={cnKyc('subtitle')}>2. Objectives of the Policy</h2>
        <ul className={cnKyc('list')}>
          <li className={cnKyc('item')}>
            <span>Compliance:</span> To ensure adherence to local and
            international KYC and AML regulations.
          </li>
          <li className={cnKyc('item')}>
            <span>Risk Mitigation:</span> To protect the company from risks
            associated with financial crimes.
          </li>
          <li className={cnKyc('item')}>
            <span>Customer Trust:</span> To foster a secure environment for our
            customers through transparent practices.
          </li>
          <li className={cnKyc('item')}>
            <span>Training and Awareness:</span> To educate employees about
            KYC/AML obligations and procedures.
          </li>
        </ul>
        <h2 className={cnKyc('subtitle')}>3. KYC Procedures</h2>
        <div className={cnKyc('category')}>
          <span>3.1</span> Customer Identification
        </div>
        <ul className={cnKyc('list')}>
          <li className={cnKyc('item')}>
            <span>Individual Customers:</span> All individual customers must
            provide:
            <ul className={cnKyc('list')}>
              <li className={cnKyc('item')}>
                Government-issued photo identification (e.g., passport, national
                ID card).
              </li>
              <li className={cnKyc('item')}>
                Proof of address (e.g., utility bill, bank statement) dated
                within the last three months.
              </li>
              <li className={cnKyc('item')}>Date of birth and nationality.</li>
            </ul>
          </li>
          <li className={cnKyc('item')}>
            <span>Corporate Customers:</span> For legal entities, the following
            documents must be obtained:
            <ul className={cnKyc('list')}>
              <li className={cnKyc('item')}>
                Certificate of incorporation or equivalent documentation.
              </li>
              <li className={cnKyc('item')}>
                Memorandum and articles of association.
              </li>
              <li className={cnKyc('item')}>
                List of directors and beneficial owners (individuals who own or
                control 25% or more of the company).
              </li>
              <li className={cnKyc('item')}>
                Tax identification number or equivalent.
              </li>
            </ul>
          </li>
        </ul>
        <div className={cnKyc('category')}>
          <span>3.2</span> Customer Verification
        </div>
        <ul className={cnKyc('list')}>
          <li className={cnKyc('item')}>
            <span>Document Verification:</span> The authenticity of submitted
            documents will be verified using reliable sources, including
            government databases and third-party verification services.
          </li>
          <li className={cnKyc('item')}>
            <span>Identity Verification Tools:</span> Use of biometric
            verification (where applicable) and electronic verification
            solutions to enhance identity confirmation.
          </li>
        </ul>
        <div className={cnKyc('category')}>
          <span>3.3</span> Risk Assessment
        </div>
        <ul className={cnKyc('list')}>
          <li className={cnKyc('item')}>
            <span>Customer Risk Profiling:</span> Each customer will be assigned
            a risk rating (low, medium, high) based on factors such as:
            <ul className={cnKyc('list')}>
              <li className={cnKyc('item')}>
                Geographic location (countries with high risk of money
                laundering).
              </li>
              <li className={cnKyc('item')}>
                Nature of the business or occupation.
              </li>
              <li className={cnKyc('item')}>
                Transaction patterns and volumes.
              </li>
            </ul>
          </li>
          <li className={cnKyc('item')}>
            <span>Enhanced Due Diligence (EDD):</span> High-risk customers will
            undergo EDD, which includes more stringent verification measures,
            ongoing monitoring, and additional documentation requirements.
          </li>
        </ul>
        <h2 className={cnKyc('subtitle')}>4. AML Procedures</h2>
        <div className={cnKyc('category')}>
          <span>4.1</span> Transaction Monitoring
        </div>
        <ul className={cnKyc('list')}>
          <li className={cnKyc('item')}>
            <span>Automated Monitoring Systems:</span> All transactions will be
            monitored using automated systems that flag suspicious activity
            based on predefined criteria.
          </li>
          <li className={cnKyc('item')}>
            <span>Threshold Limits:</span> Transactions exceeding certain
            thresholds will trigger alerts for further review.
          </li>
        </ul>
        <div className={cnKyc('category')}>
          <span>4.2</span> Reporting Suspicious Activities
        </div>
        <ul className={cnKyc('list')}>
          <li className={cnKyc('item')}>
            <span>Internal Reporting Mechanism:</span> Employees must report any
            suspicious transactions or activities to the designated Compliance
            Officer immediately.
          </li>
          <li className={cnKyc('item')}>
            <span>External Reporting:</span> The Compliance Officer is
            responsible for reporting suspicious activities to relevant
            authorities in accordance with local regulations.
          </li>
        </ul>
        <div className={cnKyc('category')}>
          <span>4.3</span> Record Keeping
        </div>
        <ul className={cnKyc('list')}>
          <li className={cnKyc('item')}>
            <span>Documentation Retention:</span> All KYC documentation and
            transaction records will be retained for a minimum of five years
            after the end of the customer relationship or as required by law.
          </li>
          <li className={cnKyc('item')}>
            <span>Access Control:</span> Access to KYC records will be limited
            to authorized personnel only, ensuring confidentiality and data
            protection.
          </li>
        </ul>
        <div className={cnKyc('category')}>
          <span>4.4</span> Employee Training
        </div>
        <ul className={cnKyc('list')}>
          <li className={cnKyc('item')}>
            <span>Regular Training Programs:</span> All employees will receive
            training on KYC/AML policies, red flags for suspicious activities,
            and reporting procedures at least annually.
          </li>
          <li className={cnKyc('item')}>
            <span>Updates on Regulations:</span> Employees will be informed
            about changes in KYC/AML regulations and company policies promptly.
          </li>
        </ul>
        <h2 className={cnKyc('subtitle')}>5. Data Protection and Privacy</h2>
        <ul className={cnKyc('list')}>
          <li className={cnKyc('item')}>
            <span>Compliance with Data Protection Laws:</span> All customer data
            will be handled in compliance with applicable data protection
            legislation (e.g., GDPR).
          </li>
          <li className={cnKyc('item')}>
            <span>Data Security Measures:</span> Implement appropriate security
            measures to protect customer information from unauthorized access or
            breaches.
          </li>
        </ul>
        <h2 className={cnKyc('subtitle')}>6. Review and Update of Policy</h2>
        <ul className={cnKyc('list')}>
          <li className={cnKyc('item')}>
            <span>Policy Review Frequency:</span> This policy will be reviewed
            annually or as needed in response to changes in legislation,
            regulatory guidance, or operational changes.
          </li>
          <li className={cnKyc('item')}>
            <span>Management Approval:</span> Any amendments to the policy must
            be approved by senior management.
          </li>
        </ul>
        <h2 className={cnKyc('subtitle')}>7. Conclusion</h2>
        <p className={cnKyc('text')}>
          exstream.cc is dedicated to maintaining the highest standards of
          compliance with KYC and AML regulations. By implementing this policy,
          we aim to protect our customers, our business, and the broader
          financial system from illicit activities.
        </p>
      </div>
      <Footer isHome />
    </div>
  )
}
